import { useCallback, useEffect, useState } from 'react'
import { Button, Chip } from '@heroui/react'
import { api } from '../lib/api'
import { mayWriteSetup, publisherLabel } from '../lib/branchConfig'
import { explain } from '../lib/staffErrors'
import { formatMoment, type StaffLanguage } from '../lib/staffText'
import type { BranchSetup, SetupVersion } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { Notice } from './settingsKit'
import { SetupModal } from './setupKit'
import { showToast } from '../ui/toast'
import { SETUP_CHANGED, setupChanged } from './setupChanged'
import { useMayRead, useSession } from '../ui/Session'
import '../styles/admin.css'
import '../styles/equipment.css'

/** The version this branch's floor runs, and its history; Bring back puts an old version live. */

/** The branch's setup as it stands, read again whenever a save announces itself. */
function useBranchConfig(wanted: string) {
  const { lang } = useStaffText()
  // Nobody is asked for a document the server would refuse them (lib/adminReads).
  const branchId = useMayRead('branchConfig') ? wanted : ''
  const [config, setConfig] = useState<BranchSetup | null>(null)
  const [error, setError] = useState('')

  const reload = useCallback(async () => {
    if (!branchId) return
    try {
      const answer = await api.branchSetup(branchId)
      setConfig(answer.config)
      setError('')
    } catch (err) {
      setError(explain(err, lang, 'admin.publish.notRead'))
    }
  }, [branchId, lang])

  useEffect(() => {
    setConfig(null)
    void reload()
  }, [reload])

  useEffect(() => {
    const onChange = () => void reload()
    window.addEventListener(SETUP_CHANGED, onChange)
    return () => window.removeEventListener(SETUP_CHANGED, onChange)
  }, [reload])

  return { config, error, reload, setError }
}

export function BranchVersion({
  branchId,
  onDocumentReplaced,
}: {
  branchId: string
  /** Bring back replaces the setup; the page reads it again. */
  onDocumentReplaced?: () => void
}) {
  const { lang, say } = useStaffText()
  const session = useSession()
  const mayWrite = !session || mayWriteSetup(session.identity)
  const { config, error, reload, setError } = useBranchConfig(branchId)
  const [busy, setBusy] = useState(false)
  const [history, setHistory] = useState(false)

  async function bringBack(version: number) {
    setBusy(true)
    setError('')
    try {
      await api.restoreBranchSetup(branchId, version)
      setHistory(false)
      showToast(say('eq.branch.bringBackDone', { v: version }))
      window.dispatchEvent(setupChanged())
      void reload()
      onDocumentReplaced?.()
    } catch (err) {
      setError(explain(err, lang, 'admin.publish.notBroughtBack'))
    } finally {
      setBusy(false)
    }
  }

  if (!config) return error ? <Notice>{error}</Notice> : null

  const versionLabel =
    !config.published || config.version < 1
      ? say('admin.config.neverPublished')
      : say('admin.publish.versionLive', { version: config.version })

  return (
    <>
      <span title={say('admin.publish.history')}>
        <Button variant="ghost" onPress={() => setHistory(true)}>
          <Icon name="clock" size={16} />
          {versionLabel}
        </Button>
      </span>
      {history && (
        <History
          branchId={branchId}
          live={config.version}
          busy={busy}
          error={error}
          mayBringBack={mayWrite}
          onBringBack={(version) => void bringBack(version)}
          onClose={() => {
            setHistory(false)
            setError('')
          }}
        />
      )}
    </>
  )
}

/** The versions that have been live, newest first; Bring back puts one live again. */
function History({
  branchId,
  live,
  busy,
  error,
  mayBringBack,
  onBringBack,
  onClose,
}: {
  branchId: string
  live: number
  busy: boolean
  error: string
  mayBringBack: boolean
  onBringBack: (version: number) => void
  onClose: () => void
}) {
  const { lang, say } = useStaffText()
  const [versions, setVersions] = useState<SetupVersion[] | null>(null)
  const [asking, setAsking] = useState(0)

  useEffect(() => {
    api
      .branchSetupVersions(branchId)
      .then((r) => setVersions(r.versions ?? []))
      .catch(() => setVersions([]))
  }, [branchId])

  return (
    <SetupModal title={say('admin.publish.history')} onClose={onClose} error={error} hideSubmit wide>
      {versions === null ? (
        <p className="empty" role="status">
          {say('common.loading')}
        </p>
      ) : versions.length === 0 ? (
        <p className="empty">{say('admin.publish.neverPublished')}</p>
      ) : (
        <ul className="admin-history">
          {versions.map((v) => (
            <li key={v.version}>
              <span className="admin-history__v">v{v.version}</span>
              <span className="admin-history__what">
                <strong>{v.note || when(v.publishedAt, lang)}</strong>
                <span>
                  {v.note ? `${when(v.publishedAt, lang)} ` : ''}
                  {publisherLabel(v.publishedBy, v.publishedByName)}
                </span>
                <span className="eq-version">
                  {say('eq.branch.versionOf', {
                    op: v.operation.name,
                    n: v.operation.revision,
                    eq: v.equipment.name,
                    m: v.equipment.revision,
                  })}
                </span>
              </span>
              {v.version === live ? (
                <Chip size="sm" variant="soft" color="success">
                  {say('admin.publish.onFloor')}
                </Chip>
              ) : !mayBringBack ? (
                <span />
              ) : asking === v.version ? (
                <span className="eq-ask" role="alertdialog">
                  <strong>{say('eq.branch.bringBackAsk', { v: v.version })}</strong>
                  <Button variant="ghost" size="sm" autoFocus onPress={() => setAsking(0)}>
                    {say('common.cancel')}
                  </Button>
                  <Button variant="primary" size="sm" isDisabled={busy} onPress={() => onBringBack(v.version)}>
                    {say('admin.publish.bringBack')}
                  </Button>
                </span>
              ) : (
                <Button variant="secondary" size="sm" isDisabled={busy} onPress={() => setAsking(v.version)}>
                  {say('admin.publish.bringBack')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </SetupModal>
  )
}

/** A moment, in the reader's own clock. */
function when(at: string, lang: StaffLanguage): string {
  return formatMoment(at, lang, { date: true, time: true })
}
